import { defineStore } from "pinia";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import i18n from "@/Plugins/i18n";

import type { PrivacyState } from "./Models/State";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";
import { LocalStorageNamespace } from "@/Modules/App/Models/Localstorage/LocalStorageNamespace";
import { LocalStorageKey } from "@/Modules/App/Models/Localstorage/LocalStorageKey";

import { LocalStorageService } from "@/Services/LocalStorageService";

import { config } from "@/Common/envConfig";

import { isApp } from "@/Utils/Ionic";
import { isValidString } from "@/Utils/String";
import { unixUTC } from "@/Utils/Date";

const privacyStorage = new LocalStorageService(LocalStorageNamespace.PRIVACY);

const privacyState: PrivacyState = {
  isPrivate: isApp() && isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_PIN_CODE)),
};

export const usePrivacyStore = defineStore(StateNamespace.PRIVACY, {
  state: () => ({ ...privacyState }),
  getters: {},
  actions: {
    clear() {
      this.setIsPrivate(false);
    },

    setIsPrivate(val: boolean) {
      this.isPrivate = val;

      if (val === false && isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_PIN_CODE))) {
        privacyStorage.delete(LocalStorageKey.PRIVACY_PIN_CODE);
        privacyStorage.delete(LocalStorageKey.PRIVACY_DATE);
        privacyStorage.delete(LocalStorageKey.PRIVACY_BIO);
        privacyStorage.delete(LocalStorageKey.PRIVACY_BIO_CHECKED);
      }
    },

    validatePin(newPin: string): boolean {
      return privacyStorage.get(LocalStorageKey.PRIVACY_PIN_CODE) === newPin;
    },

    updatePinDate() {
      privacyStorage.set(LocalStorageKey.PRIVACY_DATE, unixUTC() + config.privacyValidSecsCode);
    },

    updatePinCode(value: string) {
      privacyStorage.set(LocalStorageKey.PRIVACY_PIN_CODE, value);
    },

    needProtection(): boolean {
      if (!this.isPrivate) return false;

      try {
        const limitDate = parseInt(privacyStorage.get(LocalStorageKey.PRIVACY_DATE), 10);

        return limitDate < unixUTC();
      } catch (e) {
        return false;
      }
    },

    isBioEnabled(): boolean {
      try {
        return isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_BIO));
      } catch (e) {
        return false;
      }
    },

    setIsBio(bool: boolean) {
      if (bool === false) {
        privacyStorage.delete(LocalStorageKey.PRIVACY_BIO);
      } else {
        privacyStorage.set(LocalStorageKey.PRIVACY_BIO, "true");
      }
    },

    async checkBiometric(hasFallback = false): Promise<boolean> {
      if (isApp()) {
        const t = i18n.global.t;
        const biometrics = await NativeBiometric.isAvailable().catch(() => ({ isAvailable: false }));

        if (biometrics.isAvailable) {
          const isVerified = await NativeBiometric.verifyIdentity({
            reason: t("settings.privacy.bio.reason"),
            title: t("settings.privacy.bio.title"),
            subtitle: t("settings.privacy.bio.subtitle"),
            description: t("settings.privacy.bio.description"),
            useFallback: hasFallback,
            maxAttempts: 2,
          })
            .then(() => true)
            .catch(() => false);

          if (this.needProtection() && this.isBioEnabled()) {
            privacyStorage.set(LocalStorageKey.PRIVACY_BIO_CHECKED, `${isVerified}`);
          } else if (this.isBioEnabled()) {
            privacyStorage.delete(LocalStorageKey.PRIVACY_BIO_CHECKED);
          }

          return isVerified;
        }

        return false;
      }

      return false;
    },

    isBioUsed(): boolean {
      return isValidString(privacyStorage.get(LocalStorageKey.PRIVACY_BIO_CHECKED));
    },
  },
});

export type PrivacyStore = ReturnType<typeof usePrivacyStore>;

export default usePrivacyStore;
